import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Sheet from '../components/Sheet';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LocaleContext';
import { DEFAULT_MECHANIC_TABS, DESK_TABS, normalizeDeskTabs } from '../lib/deskNav';
import {
  ACTION_OPTIONS,
  actionLabel,
  activitySummary,
  displayName,
  formatWhen,
  initialsFor,
  matchesActivitySearch,
  visitLabel,
  withinDateRange,
} from '../lib/technicianActivity';
import { supabase } from '../lib/supabase';

const blankMechanic = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  password: '',
  branch_id: '',
};

function matchesMechanicSearch(mechanic, needle, branchName) {
  if (!needle) return true;
  const hay = [
    displayName(mechanic),
    mechanic.email,
    mechanic.phone,
    branchName(mechanic.branch_id),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(needle);
}

function ActivityDialog({
  mechanic,
  activity,
  branches,
  branchName,
  onClose,
  onRefresh,
  loading,
}) {
  const [query, setQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('all');

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return activity.filter((row) => {
      if (mechanic && row.technician_id !== mechanic.id) return false;
      if (actionFilter && row.action !== actionFilter) return false;
      if (!withinDateRange(row.created_at, dateFilter)) return false;
      return matchesActivitySearch(row, needle);
    });
  }, [activity, mechanic, query, actionFilter, dateFilter]);

  const summary = useMemo(() => {
    const byAction = {};
    rows.forEach((row) => {
      byAction[row.action] = (byAction[row.action] || 0) + 1;
    });
    return byAction;
  }, [rows]);

  const title = mechanic ? `${displayName(mechanic)} activity` : 'All mechanic activity';
  const subtitle = mechanic
    ? [mechanic.email, branchName(mechanic.branch_id), mechanic.phone].filter(Boolean).join(' · ')
    : 'Every mechanic on your team';

  return (
    <Sheet
      variant="modal"
      wide
      title={title}
      subtitle={subtitle}
      onClose={onClose}
    >
      <div className="mechanic-toolbar mechanic-toolbar-dialog">
        <label className="search-grow">
          <span className="sr-only">Search activity</span>
          <input
            className="search"
            type="search"
            placeholder="Search summary, visit, vehicle…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <label className="inline-filter">
          Action
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            {ACTION_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-filter">
          When
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </label>
        <button type="button" className="ghost" onClick={onRefresh}>
          Refresh
        </button>
      </div>

      {Object.keys(summary).length > 0 ? (
        <div className="chip-row mechanic-dialog-chips">
          {Object.entries(summary).map(([action, count]) => (
            <span key={action} className="pill">
              {actionLabel(action)} · {count}
            </span>
          ))}
        </div>
      ) : null}

      {loading ? (
        <p className="empty">Loading activity…</p>
      ) : rows.length === 0 ? (
        <p className="empty">
          {mechanic
            ? `${displayName(mechanic)} has no activity matching these filters.`
            : 'No activity matches these filters.'}
        </p>
      ) : (
        <div className="activity-feed activity-feed-dialog">
          {rows.map((row) => (
            <article key={row.id} className="activity-feed-item">
              <div className="activity-feed-head">
                <span className="pill">{actionLabel(row.action)}</span>
                <time>{formatWhen(row.created_at)}</time>
              </div>
              <p>{activitySummary(row)}</p>
              <div className="activity-feed-meta">
                {!mechanic ? <span>{row.technician_name}</span> : null}
                {row.booking_id ? (
                  <Link to={`/bookings/${row.booking_id}`} onClick={onClose}>
                    Open {visitLabel(row)}
                  </Link>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}
    </Sheet>
  );
}

export default function TechnicianActivityPage() {
  const t = useT();
  const { isManager } = useAuth();
  const [mechanics, setMechanics] = useState([]);
  const [branches, setBranches] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null);
  const [activityDialog, setActivityDialog] = useState(null);
  const [draft, setDraft] = useState(blankMechanic);
  const [accessDraft, setAccessDraft] = useState([...DEFAULT_MECHANIC_TABS]);
  const [saving, setSaving] = useState(false);
  const [passwordFor, setPasswordFor] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const branchName = useMemo(() => {
    const map = Object.fromEntries(branches.map((item) => [item.id, item.name]));
    return (id) => map[id] || 'Unassigned';
  }, [branches]);

  const statsByMechanic = useMemo(() => {
    const map = new Map();
    activity.forEach((row) => {
      const current = map.get(row.technician_id) || {
        count: 0,
        lastAt: null,
      };
      current.count += 1;
      if (!current.lastAt || new Date(row.created_at) > new Date(current.lastAt)) {
        current.lastAt = row.created_at;
      }
      map.set(row.technician_id, current);
    });
    return map;
  }, [activity]);

  const filteredMechanics = useMemo(() => {
    const needle = query.trim().toLowerCase();
    let rows = mechanics.filter((mechanic) => {
      if (branchFilter && mechanic.branch_id !== branchFilter) return false;
      const stats = statsByMechanic.get(mechanic.id) || { count: 0 };
      if (activityFilter === 'active' && stats.count === 0) return false;
      if (activityFilter === 'idle' && stats.count > 0) return false;
      return matchesMechanicSearch(mechanic, needle, branchName);
    });

    rows = [...rows].sort((a, b) => {
      const statsA = statsByMechanic.get(a.id) || { count: 0, lastAt: null };
      const statsB = statsByMechanic.get(b.id) || { count: 0, lastAt: null };
      if (sortBy === 'activity') return statsB.count - statsA.count;
      if (sortBy === 'recent') {
        const timeA = statsA.lastAt ? new Date(statsA.lastAt).getTime() : 0;
        const timeB = statsB.lastAt ? new Date(statsB.lastAt).getTime() : 0;
        return timeB - timeA;
      }
      return displayName(a).localeCompare(displayName(b));
    });

    return rows;
  }, [mechanics, query, branchFilter, activityFilter, sortBy, statsByMechanic, branchName]);

  const teamStats = useMemo(() => {
    const withActivity = mechanics.filter(
      (mechanic) => (statsByMechanic.get(mechanic.id)?.count || 0) > 0
    ).length;
    return {
      total: mechanics.length,
      withActivity,
      idle: mechanics.length - withActivity,
      actions: activity.length,
    };
  }, [mechanics, statsByMechanic, activity.length]);

  async function load() {
    setLoading(true);
    setError('');
    const [
      { data: mechanicRows, error: mechanicError },
      { data: branchRows, error: branchError },
      { data: activityRows, error: activityError },
    ] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, first_name, last_name, full_name, phone, email, role, branch_id, desk_tabs')
        .eq('role', 'technician')
        .order('full_name'),
      supabase.from('branches').select('id, name').order('name'),
      supabase.rpc('get_technician_activity_log', {
        p_technician_id: null,
        p_limit: 500,
        p_offset: 0,
      }),
    ]);
    setLoading(false);
    if (mechanicError || branchError || activityError) {
      setError(mechanicError?.message || branchError?.message || activityError?.message);
      return;
    }
    setMechanics(mechanicRows || []);
    setBranches(branchRows || []);
    setActivity(activityRows || []);
  }

  useEffect(() => {
    load();
  }, []);

  function openCreate() {
    setDraft({
      ...blankMechanic,
      branch_id: branches[0]?.id || '',
    });
    setAccessDraft([...DEFAULT_MECHANIC_TABS]);
    setEditor({ mode: 'create' });
    setError('');
  }

  function openEdit(mechanic, event) {
    event?.stopPropagation();
    setDraft({
      first_name: mechanic.first_name || '',
      last_name: mechanic.last_name || '',
      email: mechanic.email || '',
      phone: mechanic.phone || '',
      password: '',
      branch_id: mechanic.branch_id || '',
    });
    setAccessDraft(normalizeDeskTabs(mechanic.desk_tabs) || [...DEFAULT_MECHANIC_TABS]);
    setEditor({ mode: 'edit', id: mechanic.id });
    setError('');
  }

  function openActivity(mechanic = null) {
    setActivityDialog({ mechanic });
  }

  function toggleAccessTab(id) {
    setAccessDraft((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  }

  function openPasswordReset(mechanic, event) {
    event?.stopPropagation();
    setPasswordFor(mechanic);
    setNewPassword('');
    setResetError('');
    setError('');
    setNotice('');
  }

  async function resetPassword(event) {
    event.preventDefault();
    if (!isManager || !passwordFor) return;
    if (newPassword.trim().length < 8) {
      setResetError(t('team.passwordMin'));
      return;
    }
    setResetting(true);
    setResetError('');
    const { error: rpcError } = await supabase.rpc('update_staff', {
      p_staff_id: passwordFor.id,
      p_password: newPassword.trim(),
    });
    setResetting(false);
    if (rpcError) {
      setResetError(rpcError.message);
      return;
    }
    setPasswordFor(null);
    setNewPassword('');
    setNotice(t('team.resetOk'));
  }

  async function saveMechanic(event) {
    event.preventDefault();
    if (!isManager) return;
    const tabs = normalizeDeskTabs(accessDraft);
    if (!tabs || tabs.length === 0) {
      setError('Pick at least one desk tab for this mechanic.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editor?.mode === 'create') {
        const { data: newId, error: createError } = await supabase.rpc('create_staff', {
          p_email: draft.email.trim(),
          p_password: draft.password,
          p_first_name: draft.first_name.trim(),
          p_last_name: draft.last_name.trim(),
          p_phone: draft.phone.trim(),
          p_role: 'technician',
          p_branch_id: draft.branch_id || null,
        });
        if (createError) throw createError;
        if (newId) {
          const { error: tabsError } = await supabase
            .from('profiles')
            .update({ desk_tabs: tabs })
            .eq('id', newId);
          if (tabsError) throw tabsError;
        }
      } else if (editor?.mode === 'edit') {
        const { error: updateError } = await supabase.rpc('update_staff', {
          p_staff_id: editor.id,
          p_first_name: draft.first_name.trim(),
          p_last_name: draft.last_name.trim(),
          p_phone: draft.phone.trim(),
          p_email: draft.email.trim(),
          p_password: draft.password.trim() || null,
          p_branch_id: draft.branch_id || null,
          p_desk_tabs: tabs,
        });
        if (updateError) throw updateError;
      }
      setEditor(null);
      await load();
    } catch (saveError) {
      setError(saveError?.message || 'Could not save mechanic.');
    } finally {
      setSaving(false);
    }
  }

  async function removeMechanic(mechanic, event) {
    event?.stopPropagation();
    if (!isManager) return;
    if (!window.confirm(`Remove ${displayName(mechanic)} as a mechanic?`)) return;
    setSaving(true);
    setError('');
    const { error: saveError } = await supabase
      .from('profiles')
      .update({ role: 'customer', branch_id: null, desk_tabs: null })
      .eq('id', mechanic.id);
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    if (activityDialog?.mechanic?.id === mechanic.id) setActivityDialog(null);
    load();
  }

  return (
    <section>
      <header className="page-head">
        <div>
          <span className="kicker">{t('mechanic.kicker')}</span>
          <h1>{t('mechanic.title')}</h1>
          <p className="lede">
            {t('mechanic.lede')}
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="ghost" onClick={() => openActivity(null)}>
            {t('mechanic.allActivity')}
          </button>
          {isManager ? (
            <button type="button" className="primary" onClick={openCreate}>
              {t('mechanic.add')}
            </button>
          ) : null}
        </div>
      </header>

      <div className="stat-grid compact">
        <div className="stat-card">
          <span>{teamStats.total}</span>
          <strong>{t('mechanic.mechanics')}</strong>
        </div>
        <div className="stat-card">
          <span>{teamStats.withActivity}</span>
          <strong>{t('mechanic.withActivity')}</strong>
        </div>
        <div className="stat-card">
          <span>{teamStats.idle}</span>
          <strong>No activity yet</strong>
        </div>
        <div className="stat-card">
          <span>{teamStats.actions}</span>
          <strong>Total actions</strong>
        </div>
      </div>

      <div className="mechanic-toolbar">
        <label className="search-grow">
          <span className="sr-only">Search mechanics</span>
          <input
            className="search"
            type="search"
            placeholder="Search name, email, phone, branch…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <label className="inline-filter">
          Branch
          <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
            <option value="">All branches</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-filter">
          Activity
          <select value={activityFilter} onChange={(e) => setActivityFilter(e.target.value)}>
            <option value="all">All mechanics</option>
            <option value="active">With activity</option>
            <option value="idle">No activity yet</option>
          </select>
        </label>
        <label className="inline-filter">
          Sort
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Name A–Z</option>
            <option value="activity">Most active</option>
            <option value="recent">Recently active</option>
          </select>
        </label>
        <button type="button" className="ghost" onClick={load}>
          Refresh
        </button>
      </div>

      {error ? <p className="form-error">{error}</p> : null}
      {notice ? <p className="form-ok">{notice}</p> : null}

      {loading ? (
        <p className="empty">Loading mechanics…</p>
      ) : mechanics.length === 0 ? (
        <p className="empty">
          No mechanics yet.
          {isManager ? ' Add the first mechanic to get started.' : ''}
        </p>
      ) : filteredMechanics.length === 0 ? (
        <p className="empty">No mechanics match your filters.</p>
      ) : (
        <div className="mechanic-card-grid">
          {filteredMechanics.map((mechanic) => {
            const stats = statsByMechanic.get(mechanic.id) || {
              count: 0,
              lastAt: null,
            };
            return (
              <article key={mechanic.id} className="mechanic-card">
                <button
                  type="button"
                  className="mechanic-card-main"
                  onClick={() => openActivity(mechanic)}
                >
                  <div className="mechanic-card-head">
                    <span className="mechanic-avatar">{initialsFor(mechanic)}</span>
                    <div>
                      <strong>{displayName(mechanic)}</strong>
                      <p className="mechanic-card-meta">{mechanic.email || 'No email'}</p>
                    </div>
                  </div>
                  <p className="mechanic-card-meta">
                    {branchName(mechanic.branch_id)}
                    {mechanic.phone ? ` · ${mechanic.phone}` : ''}
                  </p>
                  <div className="mechanic-card-stats">
                    <span className="pill">
                      {stats.count > 0 ? `${stats.count} actions` : 'No activity yet'}
                    </span>
                    {stats.lastAt ? (
                      <span className="pill">Last {formatWhen(stats.lastAt)}</span>
                    ) : null}
                  </div>
                  <span className="mechanic-card-cta">View activity</span>
                </button>
                {isManager ? (
                  <div className="row-actions mechanic-card-actions">
                    <button
                      type="button"
                      className="ghost"
                      onClick={(event) => openEdit(mechanic, event)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      onClick={(event) => openPasswordReset(mechanic, event)}
                    >
                      {t('team.resetPassword')}
                    </button>
                    <button
                      type="button"
                      className="ghost danger"
                      onClick={(event) => removeMechanic(mechanic, event)}
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {activityDialog ? (
        <ActivityDialog
          mechanic={activityDialog.mechanic}
          activity={activity}
          branches={branches}
          branchName={branchName}
          loading={loading}
          onClose={() => setActivityDialog(null)}
          onRefresh={load}
        />
      ) : null}

      {editor && isManager ? (
        <Sheet
          variant="modal"
          title={editor.mode === 'create' ? 'Add mechanic' : 'Edit mechanic'}
          subtitle="Mechanics"
          onClose={() => setEditor(null)}
        >
          <form className="sheet-form" onSubmit={saveMechanic}>
            <p className="lede" style={{ marginTop: 0 }}>
              They sign in to the mobile app with this email and password.
            </p>
            <div className="form-row">
              <label>
                First name
                <input
                  required
                  value={draft.first_name}
                  onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
                />
              </label>
              <label>
                Last name
                <input
                  value={draft.last_name}
                  onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
                />
              </label>
            </div>
            <label>
              Email
              <input
                required
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </label>
            <label>
              Phone
              <input
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </label>
            <label>
              {editor.mode === 'create' ? 'Password' : 'New password (optional)'}
              <input
                required={editor.mode === 'create'}
                type="text"
                minLength={editor.mode === 'create' ? 8 : 0}
                autoComplete="new-password"
                value={draft.password}
                onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                placeholder={editor.mode === 'edit' ? 'Leave blank to keep current password' : ''}
              />
            </label>
            <label>
              Branch
              <select
                required
                value={draft.branch_id}
                onChange={(e) => setDraft({ ...draft, branch_id: e.target.value })}
              >
                <option value="">Unassigned</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="access-block">
              <strong>Desk tabs</strong>
              <p className="lede" style={{ marginTop: 6 }}>
                Choose which console tabs this mechanic can open.
              </p>
              <ul className="access-list">
                {DESK_TABS.map((tab) => {
                  const on = accessDraft.includes(tab.id);
                  return (
                    <li key={tab.id}>
                      <label className={on ? 'access-row on' : 'access-row'}>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleAccessTab(tab.id)}
                        />
                        <span className="material-symbol" aria-hidden="true">
                          {tab.icon}
                        </span>
                        <strong>{t(tab.labelKey)}</strong>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="row-actions">
              <button type="submit" className="primary" disabled={saving}>
                {saving ? 'Saving…' : editor.mode === 'create' ? 'Create login' : 'Save changes'}
              </button>
              <button type="button" className="ghost" onClick={() => setEditor(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Sheet>
      ) : null}

      {passwordFor && isManager ? (
        <Sheet
          variant="modal"
          title={t('team.resetPasswordFor', { name: displayName(passwordFor) })}
          subtitle={t('mechanic.kicker')}
          onClose={() => setPasswordFor(null)}
        >
          <form className="sheet-form" onSubmit={resetPassword}>
            <p className="lede" style={{ marginTop: 0 }}>
              {t('team.passwordHint')}
            </p>
            {resetError ? <p className="form-error">{resetError}</p> : null}
            <label>
              {t('team.newPassword')}
              <input
                required
                type="text"
                minLength={8}
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </label>
            <div className="row-actions">
              <button type="submit" className="primary" disabled={resetting}>
                {resetting ? t('team.resetting') : t('team.resetPassword')}
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => setPasswordFor(null)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </Sheet>
      ) : null}
    </section>
  );
}
